import { ChartViewType, AllCyclesResponse, AllCyclesSeries } from '@/api/dashboard/types';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { QueryDateRange } from '../../../types';
import {
	formatDayMonthLabel,
	formatDayNameLabel,
	formatMonthLabel,
	generateDaysRangeLabels,
	generateMonthsRangeLabels,
	generateWeekRangeLabels,
	transformSeriesDataset,
	normalizeDataset,
	generateOneDayRangeLabels,
	formatHoursLabel,
	LabelInfo,
	AllCyclesDataset,
} from './utils';

interface AllCyclesChartData {
	labels?: LabelInfo[];
	dataset?: AllCyclesDataset[];
}

const normalizeData = (
	series: AllCyclesSeries[] | undefined,
	formatFn: (date: dayjs.Dayjs, start: dayjs.Dayjs, end: dayjs.Dayjs) => string,
	labels: LabelInfo[],
	startDate: dayjs.Dayjs,
	endDate: dayjs.Dayjs
) => {
	const normalizedSeries = series?.reduce((acc, series) => {
		const dataset = transformSeriesDataset(formatFn, startDate, endDate, series);
		const normalizedDataset = normalizeDataset(labels, dataset);
		acc.push(normalizedDataset);
		return acc;
	}, [] as AllCyclesDataset[]);
	return normalizedSeries;
};

export const useAllCyclesChartData = (
	allCycles?: AllCyclesResponse,
	dateRange?: QueryDateRange
): AllCyclesChartData => {
	const [labels, setLabels] = useState<LabelInfo[]>();
	const [dataset, setDataset] = useState<AllCyclesDataset[]>();

	useEffect(() => {
		const { data: series, view } = allCycles || {};
		const { start, end } = dateRange || {};

		if (!start || !end) return;

		const startDate = dayjs(start);
		const endDate = dayjs(end);
		const dayDifference = endDate?.diff(startDate, 'd');

		if (view === ChartViewType.Hours) {
			const filteredSeries = series?.map((s) => ({
				...s,
				points: s.points.filter((log) => dayjs(log.date).isSame(startDate, 'day')),
			}));

			const labels = generateOneDayRangeLabels(startDate, endDate);
			const normalizedSeries = normalizeData(
				filteredSeries,
				formatHoursLabel,
				labels,
				startDate,
				endDate
			);

			setLabels(labels);
			setDataset(normalizedSeries);
			return;
		}

		if (view === ChartViewType.Days) {
			const formatFn = dayDifference === 6 ? formatDayNameLabel : formatDayMonthLabel;
			const labels = generateDaysRangeLabels(startDate, endDate);
			const normalizedSeries = normalizeData(series, formatFn, labels, startDate, endDate);

			setLabels(labels);
			setDataset(normalizedSeries);
			return;
		}

		if (view === ChartViewType.Weeks) {
			const labels = generateWeekRangeLabels(startDate, endDate);
			const normalizedSeries = normalizeData(
				series,
				formatDayMonthLabel,
				labels,
				startDate,
				endDate
			);

			setLabels(labels);
			setDataset(normalizedSeries);
			return;
		}

		const labels = generateMonthsRangeLabels(startDate, endDate);
		const normalizedSeries = normalizeData(series, formatMonthLabel, labels, startDate, endDate);

		setLabels(labels);
		setDataset(normalizedSeries);
	}, [allCycles, dateRange]);

	return { labels, dataset };
};
